import { ref } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import { useRouter } from '@core/utils/utils'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default function useEventsUpload() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { formatDatePicker } = useCommon()
  const { router } = useRouter()
  const { getItems, createItems, deleteItems } = useLambda()
  const { confirmationPlanningGroupByRole } = useMongoDBFunctions()
  const projectStartDate = ref(new Date())
  const filterBy = ref("workers")
  const filterOptions = ref([
    { title: i18n.t('label.workers'), value: "workers" },
    { title: i18n.t('label.roles_locations'), value: "roles/locations" }
  ])
  const selectedWorkers = ref([])
  const selectedRoles = ref([])
  const selectedLocations = ref([])
  const includeWeekends = ref(false)
  const clientFunctionality = localStorage.getItem("clientData") ? JSON.parse(localStorage.clientData) : false

  const bulkEventsUpload = async (workers, pushRoute = true) => {
    const getUserData = store.state?.userStore?.userData
    const client_id = getUserData.client?.$oid
    const worker_id = getUserData.worker_id?.$oid
    const filterData = {
      workers,
      roles: [...selectedRoles.value],
      locations: [...selectedLocations.value],
    }

    // Adjust date format
    const formattedStartDate = formatDatePicker(projectStartDate.value)
    
    try {
      const workersWithRoles = await getWorkersRoles(filterBy.value, filterData, client_id)

      const rolesData = await getConfirmationsPlannings(client_id)
      
      const roleCycles = generateRoleCycles(rolesData)

      let workersIds = []
      let workersData = []

      for (const worker of workersWithRoles) {
        if (!worker.roles?.length) continue
        workersIds.push(worker._id)
        for (const role of worker.roles) {
          if (!roleCycles.hasOwnProperty(role)) {
            console.log(`Could not find role with ID ${role} in the list of roles`)
            continue
          }

          const personalCycle = generatePersonalCycle(roleCycles[role])

          workersData.push({
            ...worker,
            personalCycle,
          })
        }
      }

      await deleteFutureEvents(workersIds, formattedStartDate)

      await createEvents(workersData, client_id, worker_id, formattedStartDate)

      if (pushRoute) {
        if (clientFunctionality.default_view_pc_team) router.push({ name: 'apps-pc-team' })
        else if (clientFunctionality.default_view_daily_dialogue) router.push({ name: 'habit-meeting-new' })
        else router.push({ name: 'apps-calendar' })
      }
    } catch (error) {
      console.log(error)
    }
  }

  const getWorkersRoles = (filterBy, filterData, clientId) => {
    let conditions
    if (filterBy === "workers") {
      conditions = { _id: { $in: filterData.workers.map(e => oid(e)) } }
    } else {
      conditions = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      if (filterData.locations.length > 0) {
        conditions.locations = { $in: filterData.locations.map(e => oid(e)) }
      }

      if (filterData.roles.length > 0) {
        conditions.roles = { $in: filterData.roles.map(e => oid(e)) }
      }
    }

    return new Promise(async (resolve, reject) => {
      try {
        const items = await getItems({ collection: 'worker', query: conditions, options: { projection : { roles: 1, supervisors: 1, locations: 1 } } })
  
        if (!items) {
          showErrorMessage(i18n.t('message.err_no_employees_found'))
          reject("Employees not found")
        }

        resolve(items)
      } catch (error) {
        showErrorMessage(i18n.t('message.err_worker_list'));
        reject(error)
      }
    })
  }

  const getConfirmationsPlannings = async (client_id) => {
    return new Promise(async (resolve, reject) => {
      try {
        const input = { client_id }
      
        const items = await confirmationPlanningGroupByRole(input)
  
        if (!items?.rolesData?.length) {
          showErrorMessage(i18n.t('message.err_empty_confirmation_planning'))
          reject("Confirmations plannings not found")
        }
  
        resolve(items.rolesData)
      } catch (error) {
        showErrorMessage(i18n.t('message.err_confirmation_planning'))
        reject(error)
      }
    })
  }

  const generateRoleCycles = (rolesData) => {
    let roleCycles = {}
    rolesData.forEach(role => {
      const lengthIteration = role.planning[0].confirmationsPerYear
      let cycle = []
      for (let i = 0; i < lengthIteration; i++) {
        role.planning.forEach(plan => {
          if (plan.confirmationsPerYear > 0) {
            cycle.push({
              processId: plan.process,
              locations: plan.locations || []
            })
            plan.confirmationsPerYear--
          }
        })
      }
      roleCycles[role._id] = cycle
    })
    return roleCycles
  }

  const generatePersonalCycle = (roleCycle) => {
    const cycleLength = roleCycle.length
    const index = Math.floor(Math.random() * cycleLength)
    const firstPart = roleCycle.slice(0, index)
    const secondPart = roleCycle.slice(index, cycleLength)
    const personalCycle = [...secondPart, ...firstPart]
    return personalCycle
  }

  const deleteFutureEvents = async (workersIds, projectStartDate) => {
    return new Promise(async (resolve, reject) => {
      try {
        const query = {
          start: { $gte: date(projectStartDate) },
          'extendedProps.calendar': { $ne: "Realizadas" },
          attendee: { $in: workersIds.map(id => oid(id)) }
        }
  
        const { deletedCount } = await deleteItems({ collection: 'event', query })
  
        showSuccessMessage(`${deletedCount} ${i18n.t('message.old_events_deleted')}`)
        resolve()
      } catch (error) {
        showErrorMessage(i18n.t('message.err_deleting_old_events'))
        reject(error)
      }
    })
  }

  const createEvents = (workersData, client_id, worker_id, projectStartDate) => {
    let events = []

    let now = new Date()
    let firstDayOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    now = now.getTime()
    firstDayOfNextMonth = firstDayOfNextMonth.getTime()

    workersData.forEach(worker => {
      // Filtrar procesos según locations del trabajador
      const workerLocationsIds = worker.locations || []
      const filteredCycle = worker.personalCycle.filter(item => {
        const confirmationPlanningLocationsIds = item.locations || []

        // Si el proceso no tiene locations asignadas, se incluye
        if (confirmationPlanningLocationsIds.length === 0) return true

        // Si el trabajador no tiene locations, no se incluyen procesos con locations
        if (workerLocationsIds.length === 0) return false

        // Si el proceso tiene locations, debe haber al menos una coincidencia con las del trabajador
        return confirmationPlanningLocationsIds.some(processLoc => workerLocationsIds.includes(processLoc))
      })

      const cycleLength = filteredCycle.length

      // Si no hay procesos aplicables para este trabajador, continuar con el siguiente
      if (cycleLength === 0) return

      const timeSpan = Math.floor(365 / cycleLength)

      // offset inicial aleatorio dentro del primer intervalo
      const randomOffset = Math.floor(Math.random() * timeSpan)

      for (let i = 0; i < cycleLength; i++) {
        const processItem = filteredCycle[i]

        let eventStartDate = new Date(projectStartDate)
        eventStartDate.setDate(eventStartDate.getDate() + randomOffset + i * timeSpan)
        eventStartDate.setHours(12, 0, 0, 0)

        // 🔁 Ajuste por fin de semana
        if (!includeWeekends.value) {
          const day = eventStartDate.getDay()
          if (day === 0) eventStartDate.setDate(eventStartDate.getDate() + 1)
          if (day === 6) eventStartDate.setDate(eventStartDate.getDate() - 1)
        }

        let eventEndDate = new Date(eventStartDate)
        eventEndDate.setMinutes(30)

        events.push({
          client_id: oid(client_id),
          process: oid(processItem.processId),
          attendee: oid(worker._id),
          organizer: worker.supervisors?.length ? oid(worker.supervisors[0]) : oid(worker_id),
          start: date(eventStartDate),
          end: date(eventEndDate),
          extendedProps: {
            calendar: setEventStatus(now, firstDayOfNextMonth, eventStartDate.getTime()),
          },
        })
      }
    })

    // Upload events
    return new Promise(async (resolve, reject) => {
      try {
        let eventsCreated = 0

        if (events.length) {
          const { insertedIds } = await createItems({ collection: 'event', payload: events })
          
          eventsCreated = Object.values(insertedIds)?.length || 0
        }
        
        showSuccessMessage(`${eventsCreated} ${i18n.t('message.new_events_created')}`)
        resolve()
      } catch (error) {
        showErrorMessage(i18n.t('message.err_creating_new_events'))
        reject(error)
      }
    })
  }

  const setEventStatus = (now, firstDayOfNextMonth, eventStart) => {
    if (eventStart >= firstDayOfNextMonth) return "Futuras"
    if (eventStart < now) return "Vencidas"
    return "Pendientes"
  }

  return {
    bulkEventsUpload,
    projectStartDate,
    filterBy,
    filterOptions,
    selectedWorkers,
    selectedRoles,
    selectedLocations,
    includeWeekends,
  }
}
